import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { QuestionFieldOptions } from "@/features/question/types/fieldOptions";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { QuestionConditionals } from "@/types/crm/configuration/configuration";
import { FieldOptions } from "@/types/crm/tables/question";
import { CommonProperties, Prefix } from "@/types/utils";

export interface SurveyCheckResult {
  questionsToSync: QuestionCheckResult[];
  questionsToDelete?: QuestionCheckResult[];
  surveyInstance: Tables<"survey">;
  surveyTemplate: Tables<"survey_bank">;
  allInstanceQuestions: Tables<"question">[];
  allTemplateQuestions: Tables<"survey_bank_question_bank">[];
  templateCrmSystem: CrmSystemIds;
  instanceCrmSystem: CrmSystemIds;
  surveyLevelChanges?: SurveyLevelDangerousChange[];
  linkedQuestions: Record<Tables<'question'>['id'], Tables<'question'>['id'][]>;
}

export interface QuestionCheckResult {
  instanceQuestion: Tables<"question"> | null;
  templateQuestion: Tables<"survey_bank_question_bank"> | null;
  dangerousChanges: QuestionLevelDangerousChange[];
}

type NonDeterministicChangeType =
  | "TYPE_CHANGED"
  | "CONDITIONAL_LOGIC_CHANGED"
  | "FIELD_PROPERTIES_CHANGED"
  | "WAS_EDITED";

type PrefixedNonDeterministicChangeType = Prefix<
  NonDeterministicChangeType,
  "QUESTION_"
>;

type PrefixMetaDangerousChangeType =
  | "FIELD_OPTION_REMOVED"
  | "CONDITIONAL_WAS_REMOVED";

type StandaloneDangerousChangeType =
  | "INVALID_CONDITIONAL_REFERENCE"

type InstanceRiskDangerousChangeType =
  | "QUESTION_IS_MISSING_IN_INSTANCE"
  | "QUESTION_NOT_IN_TEMPLATE"
  | "INSTANCE_FIELD_OPTIONS_WOULD_BE_LOST";

export type SurveyLevelChangeType =
  | "SURVEY_CRM_SYSTEM_CHANGED"
  | "SURVEY_NAME_CHANGED"
  | "SURVEY_DESCRIPTION_CHANGED";

export type QuestionDangerousChangeType =
  | PrefixedNonDeterministicChangeType
  | InstanceRiskDangerousChangeType
  | Prefix<PrefixMetaDangerousChangeType, "QUESTION_INSTANCE_">
  | Prefix<PrefixMetaDangerousChangeType, "QUESTION_TEMPLATE_">
  | StandaloneDangerousChangeType;

interface QuestionLevelDangerousChangeBase {
  type: QuestionDangerousChangeType;
}

interface SurveyLevelDangerousChangeBase {
  type: SurveyLevelChangeType;
}

export interface QuestionMissingChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_IS_MISSING_IN_INSTANCE";
  context: {
    templateQuestion: Tables<"survey_bank_question_bank">;
  };
}

export interface QuestionNotInTemplateChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_NOT_IN_TEMPLATE";
  context: {
    question: Tables<"question">;
  };
}

export interface InstanceFieldOptionsWouldBeLostChange extends QuestionLevelDangerousChangeBase {
  type: "INSTANCE_FIELD_OPTIONS_WOULD_BE_LOST";
  context: {
    instanceFieldOptions: FieldOptions;
    templateFieldOptions?: FieldOptions | null;
    optionsThatWouldBeLost: FieldOptions;
  };
}

export interface QuestionTypeChangedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_TYPE_CHANGED";
  context: {
    instanceFieldType: string;
    templateFieldType: string;
  };
}

export interface InstanceFieldOptionRemovedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_INSTANCE_FIELD_OPTION_REMOVED";
  context: {
    removedOptions?: FieldOptions | null;
    instanceFieldOptions?: FieldOptions | null;
    templateFieldOptions?: FieldOptions | null;
  };
}

export interface TemplateFieldOptionRemovedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_TEMPLATE_FIELD_OPTION_REMOVED";
  context: {
    removedOptions?: FieldOptions | null;
    instanceFieldOptions?: FieldOptions | null;
    templateFieldOptions?: FieldOptions | null;
  };
}

export interface QuestionConditionalLogicChangedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_CONDITIONAL_LOGIC_CHANGED";
  context: {
    instanceConditionals: QuestionConditionals;
    templateConditionals: QuestionConditionals;
    changedFields: string[];
  };
}

export interface QuestionFieldOptionsChangedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_FIELD_PROPERTIES_CHANGED";
  context: {
    instanceFieldOptions: Record<keyof Omit<QuestionFieldOptions, 'field_options'|'field_type'>, unknown>;
    templateFieldOptions: Record<keyof Omit<QuestionFieldOptions, 'field_options'|'field_type'>, unknown>;
    modifiedOptions: Array<{
      option: string;
      value: unknown;
    }>;
  };
}

export interface InstanceConditionalWasRemovedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_INSTANCE_CONDITIONAL_WAS_REMOVED";
  context: {
    removedConditionals: QuestionConditionals["where"];
  };
}

export interface TemplateConditionalWasRemovedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_TEMPLATE_CONDITIONAL_WAS_REMOVED";
  context: {
    removedConditionals: QuestionConditionals["where"];
  };
}

export interface QuestionInstanceWasEditedChange extends QuestionLevelDangerousChangeBase {
  type: "QUESTION_WAS_EDITED";
  context: {
    changedProperties: Array<{
      property: string;
      instanceValue: unknown;
      templateValue: unknown;
    }>;
  };
}

export interface InvalidConditionalReferenceChange extends QuestionLevelDangerousChangeBase {
  type: "INVALID_CONDITIONAL_REFERENCE";
  context: {
    invalidReferences: Tables<"question">["id"][];
    availableQuestions: Array<
      CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>
    >;
  };
}

export interface SurveyCrmSystemChangedChange extends SurveyLevelDangerousChangeBase {
  type: "SURVEY_CRM_SYSTEM_CHANGED";
  context: {
    instanceCrmSystem: string;
    templateCrmSystem: string;
  };
}

export interface SurveyNameChangedChange extends SurveyLevelDangerousChangeBase {
  type: "SURVEY_NAME_CHANGED";
  context: {
    instanceName: string;
    templateName: string;
  };
}

export interface SurveyDescriptionChangedChange extends SurveyLevelDangerousChangeBase {
  type: "SURVEY_DESCRIPTION_CHANGED";
  context: {
    instanceDescription: string | null;
    templateDescription: string | null;
  };
}

export type QuestionLevelDangerousChange =
  | QuestionMissingChange
  | QuestionNotInTemplateChange
  | InstanceFieldOptionsWouldBeLostChange
  | QuestionTypeChangedChange
  | InstanceFieldOptionRemovedChange
  | TemplateFieldOptionRemovedChange
  | QuestionConditionalLogicChangedChange
  | QuestionFieldOptionsChangedChange
  | InstanceConditionalWasRemovedChange
  | TemplateConditionalWasRemovedChange
  | QuestionInstanceWasEditedChange
  | InvalidConditionalReferenceChange

export type SurveyLevelDangerousChange =
  | SurveyCrmSystemChangedChange
  | SurveyNameChangedChange
  | SurveyDescriptionChangedChange;


type AssertSubset<T extends U, U> = T;

export type QuestionChangesAffectingResponses = AssertSubset<
  | "INSTANCE_FIELD_OPTIONS_WOULD_BE_LOST"
  | "QUESTION_INSTANCE_FIELD_OPTION_REMOVED"
  | "QUESTION_CONDITIONAL_LOGIC_CHANGED"
  | "INVALID_CONDITIONAL_REFERENCE"
  | "QUESTION_TEMPLATE_FIELD_OPTION_REMOVED",
  QuestionDangerousChangeType
>;

export const QuestionChangesAffectingResponsesArray: QuestionChangesAffectingResponses[] = [
  "INSTANCE_FIELD_OPTIONS_WOULD_BE_LOST",
  "QUESTION_INSTANCE_FIELD_OPTION_REMOVED",
  "QUESTION_CONDITIONAL_LOGIC_CHANGED",
  "INVALID_CONDITIONAL_REFERENCE",
  "QUESTION_TEMPLATE_FIELD_OPTION_REMOVED",
] as const;